import { useQueryHelper } from '@anytag/shared-hooks';
import { Select, SocialIcon } from '@anytag/shared-atoms';
import { BasicAccountAnalyticsPayload } from '@anytag/graphql';
import { getThirtyDaysAgo, getToday } from '@anytag/shared-utils';
import { AnalyticsSupportedSocialMediaType, SelectOption } from '@anytag/shared-types';
import { useAnalyticsProfileSearch } from '../../hooks';
import { AnalyticsSelectedAccountInfo } from '../../types';

interface SupportedSocialMedias extends SelectOption<string> {
  socialMedia: AnalyticsSupportedSocialMediaType | '';
}

interface AnalyticsAccountsSelectorProps {
  brandAccount: BasicAccountAnalyticsPayload;
  analyticsAccount: AnalyticsSelectedAccountInfo;
}

const ADD_ACCOUNT = 'add_account';

export const AnalyticsAccountsSelector = ({
  analyticsAccount,
  brandAccount: { brandAccountId, socialMediaAccounts }
}: AnalyticsAccountsSelectorProps) => {
  const { t, navigate } = useQueryHelper();
  const { filter } = useAnalyticsProfileSearch();

  const allSupportedSocialMedias: SupportedSocialMedias[] = [
    {
      label: t<string>('Youtube'),
      value: String(socialMediaAccounts.youtube?.id || ''),
      socialMedia: AnalyticsSupportedSocialMediaType.youtube,
      prefixIcon: <SocialIcon size={20} type="YOUTUBE" />
    },
    {
      label: t<string>('Instagram'),
      value: String(socialMediaAccounts.instagram?.id || ''),
      socialMedia: AnalyticsSupportedSocialMediaType.instagram,
      prefixIcon: <SocialIcon size={20} type="INSTAGRAM" variant="transform" />
    },
    {
      label: t<string>('Facebook Page'),
      socialMedia: AnalyticsSupportedSocialMediaType.facebook,
      value: String(socialMediaAccounts.facebookPage?.id || ''),
      prefixIcon: <SocialIcon size={20} type="FACEBOOK" />
    },
    {
      label: t<string>('X(Twitter)'),
      value: String(socialMediaAccounts.twitter?.id || ''),
      socialMedia: AnalyticsSupportedSocialMediaType.twitter,
      prefixIcon: <SocialIcon size={20} type="TWITTER" />
    }
  ];
  const validSupportedAccountOptions = allSupportedSocialMedias.filter((option) => Boolean(option.value));
  const socialAccountOptions: SupportedSocialMedias[] = [
    ...validSupportedAccountOptions,
    ...(validSupportedAccountOptions.length < allSupportedSocialMedias.length
      ? [
          {
            label: '',
            value: ADD_ACCOUNT,
            socialMedia: '' as const,
            prefixIcon: (
              <div className="text-active">
                <span className="mr-1.5 h-4 w-4 text-center text-xl">+</span>
                <span className="text-base">{t('Add Social Account')}</span>
              </div>
            )
          }
        ]
      : [])
  ];

  const handleSocialAccountChange = (value: SupportedSocialMedias['value']) => {
    if (value === ADD_ACCOUNT) {
      navigate({ to: '/analytics/$brandAccountId', params: { brandAccountId: brandAccountId.toString() } });
    } else {
      const account = socialAccountOptions.find((acc) => acc.value === value);

      if (account) {
        navigate({
          to: '/analytics/$brandAccountId/$socialMedia/$socialMediaAccountId/dashboard',
          params: {
            brandAccountId: brandAccountId.toString(),
            socialMedia: account.socialMedia,
            socialMediaAccountId: account.value
          },
          search: {
            filter: {
              ...filter,
              // YT requires startDate and endDate but some IG tabs can have them as "", zod default values will be not set in that case
              startDate: filter.startDate || getThirtyDaysAgo(),
              endDate: filter.endDate || getToday()
            }
          }
        });
      }
    }
  };

  return (
    <Select
      className="w-54"
      clearable={false}
      options={socialAccountOptions}
      value={String(analyticsAccount.id)}
      onChange={handleSocialAccountChange}
    />
  );
};
